import { useMemo, useState } from "react";
import { Calendar, Gift, HandHeart, Phone, Wallet } from "lucide-react";
import useFormDraft from "../../hooks/useFormDraft";

export default function DonatePage() {
    const [form, setForm] = useState({
        montante: "",
        carteira: "",
        celular: "",
    });

    const {
        markEdited,
        clearDraft,
        draftModals,
        draftBanner,
    } = useFormDraft({
        namespace: "doar",
        data: form,
        setData: setForm,
        enabled: true,
        ready: true,
    });

    const [historico, setHistorico] = useState([
        { id: 1, valor: "85000.00 MT", carteira: "M-Pesa", celular: "84 123 4567", data: "18 Out 2025" },
        { id: 2, valor: "45200.00 MT", carteira: "e-Mola", celular: "86 765 4321", data: "10 Out 2025" },
    ]);

    const handleChange = (e) => {
        markEdited();
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.montante || !form.carteira || !form.celular) return;

        const novaDoacao = {
            id: historico.length + 1,
            valor: `${form.montante} MT`,
            carteira: form.carteira,
            celular: form.celular,
            data: new Date().toLocaleDateString("pt-MZ"),
        };

        setHistorico([novaDoacao, ...historico]);
        setForm({ montante: "", carteira: "", celular: "" });
        await clearDraft();
    };

    const totalDoado = useMemo(() => {
        const toNumber = (s) => {
            const n = Number(String(s).replace(/[^0-9.]/g, ""));
            return Number.isFinite(n) ? n : 0;
        };
        return historico.reduce((acc, d) => acc + toNumber(d.valor), 0);
    }, [historico]);

    return (
        <div className="w-full py-6">
            {draftModals}
            {draftBanner}
            {/* Resumo */}
            <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-uem-primary">
                            <HandHeart className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Total doado
                            </div>
                            <div className="text-lg font-semibold text-gray-800">
                                {totalDoado.toFixed(2)} MT
                            </div>
                        </div>
                    </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-uem-primary">
                            <Gift className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Doações
                            </div>
                            <div className="text-lg font-semibold text-gray-800">
                                {historico.length}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-uem-primary">
                            <Calendar className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Última doação
                            </div>
                            <div className="truncate text-sm font-medium text-gray-800">
                                {historico[0]?.data ?? "—"}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Formulário */}
                <div className="lg:col-span-1">
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="flex items-center gap-3 border-b border-gray-100 px-6 py-4">
                            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-uem-primary">
                                <HandHeart className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-800">
                                    Faça uma doação
                                </div>
                                <div className="text-xs text-gray-500">
                                    Preencha os dados para concluir a doação
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5 p-6">
                            <div>
                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Montante (MT)
                                </label>
                                <div className="relative">
                                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                        MT
                                    </span>
                                    <input
                                        type="number"
                                        name="montante"
                                        value={form.montante}
                                        onChange={handleChange}
                                        className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-11 pr-3 text-sm text-gray-700 placeholder:text-gray-400 transition-all focus:border-uem-primary focus:outline-none focus:ring-1 focus:ring-uem-primary"
                                        placeholder="100"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Carteira móvel
                                </label>
                                <div className="relative">
                                    <Wallet className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                    <select
                                        name="carteira"
                                        value={form.carteira}
                                        onChange={handleChange}
                                        className="w-full appearance-none rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-700 transition-all focus:border-uem-primary focus:outline-none focus:ring-1 focus:ring-uem-primary"
                                    >
                                        <option value="">Selecione</option>
                                        <option value="M-Pesa">M-Pesa</option>
                                        <option value="e-Mola">e-Mola</option>
                                        <option value="Mkesh">Mkesh</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Número de celular
                                </label>
                                <div className="relative">
                                    <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        name="celular"
                                        value={form.celular}
                                        onChange={handleChange}
                                        className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-3 text-sm text-gray-700 placeholder:text-gray-400 transition-all focus:border-uem-primary focus:outline-none focus:ring-1 focus:ring-uem-primary"
                                        placeholder="84 123 4567"
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-uem-primary py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-uem-primaryHover"
                            >
                                <HandHeart className="h-4 w-4" />
                                Doar
                            </button>
                        </form>
                    </div>
                </div>

                {/* Histórico */}
                <div className="lg:col-span-2">
                    <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-uem-primary">
                                <Gift className="h-4 w-4" />
                            </div>
                            <h2 className="text-sm font-semibold text-gray-800">
                                Histórico de doações
                            </h2>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                        {historico.length === 0 ? (
                            <div className="p-8 text-center">
                                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-uem-primary">
                                    <HandHeart className="h-6 w-6" />
                                </div>
                                <div className="text-sm font-medium text-gray-800">
                                    Nenhuma doação ainda
                                </div>
                                <div className="mt-1 text-sm text-gray-500">
                                    Quando fizer uma doação, ela aparecerá aqui.
                                </div>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {historico.map((d) => (
                                    <div key={d.id} className="flex items-center justify-between gap-4 p-4">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-uem-primary">
                                                    {d.carteira}
                                                </span>
                                                <span className="truncate text-sm font-semibold text-gray-800">
                                                    {d.valor}
                                                </span>
                                            </div>
                                            
                                        </div>
                                        <div className="shrink-0 text-right text-xs text-gray-500">
                                            {d.data}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
